import { availableHoursOn, buildCommittedLedger, dailyCapacityByMachine, stepHours, type CapacityLedger } from "./capacity.js";
import {
  addDays,
  assertISODate,
  assertWorkingDays,
  calendarDaysBetween,
  compareISO,
  isWorkingDay,
  previousWorkingDay,
  rollBackToWorkingDay,
  subtractWorkingDays,
  workingDaysBetween,
} from "./dates.js";
import {
  STARTED_STATUSES,
  type DailyAllocation,
  type ISODate,
  type JobStep,
  type MachiningSegment,
  type MachiningStep,
  type PostProcessSegment,
  type RiskLevel,
  type ScheduleInput,
  type ScheduleResult,
  type TimelineSegment,
} from "./types.js";

/** Stop searching backwards for capacity after this many calendar days. */
const MAX_LOOKBACK_DAYS = 5 * 366;
const EPSILON = 1e-9;

/**
 * Work backwards from the due date through each step (last to first) and
 * return the latest date the job can start, with a per-step timeline.
 *
 * Rules (see DECISIONS.md D3, D4, D10, D49):
 * - A due date on a non-working day rolls back to the previous working day.
 * - Post-process transit counts working days; lead time counts calendar days.
 * - Each machine runs `weeklyHours / workingDays.length` per working day, less whatever
 *   committed jobs already use on it that day. A step's machines run at the same time.
 * - Each step must finish on the working day before the next step starts.
 */
export function computeSchedule(input: ScheduleInput): ScheduleResult {
  const { settings, committedJobs, steps } = input;
  assertWorkingDays(settings.workingDays);
  const dueDate = assertISODate(input.dueDate, "dueDate");
  const today = assertISODate(input.today, "today");
  validateSteps(steps);

  const workingDays = settings.workingDays;
  const dailyCapacity = dailyCapacityByMachine(settings);
  const ledger = buildCommittedLedger(committedJobs);

  const effectiveDueDate = rollBackToWorkingDay(dueDate, workingDays);
  const timeline: TimelineSegment[] = [];
  let feasible = true;

  // `boundary` is the working day on which the current step must finish.
  let boundary = effectiveDueDate;

  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i]!;
    if (step.type === "post_process") {
      const segment = schedulePostProcess(step, i, boundary, workingDays);
      timeline.unshift(segment);
      boundary = previousWorkingDay(segment.start, workingDays);
    } else {
      const { segment, ok } = scheduleMachining(step, i, boundary, workingDays, dailyCapacity, ledger);
      if (!ok) feasible = false;
      timeline.unshift(segment);
      boundary = previousWorkingDay(segment.start, workingDays);
    }
  }

  const latestStartDate = timeline.length > 0 ? timeline[0]!.start : effectiveDueDate;
  const slackWorkingDays = workingDaysBetween(today, latestStartDate, workingDays);
  const status = input.status ?? "quoted";
  const riskLevel = classifyRisk({
    feasible,
    isComplete: status === "complete",
    hasStarted: STARTED_STATUSES.includes(status),
    today,
    dueDate,
    latestStartDate,
    slackWorkingDays,
    alertLeadDays: settings.alertLeadDays,
  });

  return {
    latestStartDate,
    effectiveDueDate,
    timeline,
    totalCalendarDays: calendarDaysBetween(latestStartDate, dueDate) + 1,
    slackWorkingDays,
    feasible,
    riskLevel,
  };
}

function schedulePostProcess(
  step: Extract<JobStep, { type: "post_process" }>,
  stepIndex: number,
  arriveBack: ISODate,
  workingDays: readonly number[],
): PostProcessSegment {
  // Transit back runs in working days: the vendor dispatches `transitDaysBack` working days before arrival.
  const dispatchBack = subtractWorkingDays(arriveBack, step.transitDaysBack, workingDays);
  // Lead time runs in calendar days at the vendor. Receipt at the vendor can only happen on a working day,
  // so if the lead window would need to start on a non-working day we deliver earlier (safe side).
  const arriveOut = rollBackToWorkingDay(addDays(dispatchBack, -step.leadDays), workingDays);
  // Transit out, again in working days.
  const dispatchOut = subtractWorkingDays(arriveOut, step.transitDaysOut, workingDays);

  return {
    kind: "post_process",
    stepIndex,
    name: step.name,
    start: dispatchOut,
    end: arriveBack,
    transitOut: { start: dispatchOut, end: arriveOut },
    lead: { start: arriveOut, end: dispatchBack },
    transitBack: { start: dispatchBack, end: arriveBack },
  };
}

/**
 * Each machine's share of the step runs at the same time, placed backwards from `end` on that
 * machine's own capacity. The step starts when its earliest share starts (D49).
 */
function scheduleMachining(
  step: MachiningStep,
  stepIndex: number,
  end: ISODate,
  workingDays: readonly number[],
  dailyCapacity: ReadonlyMap<string, number>,
  ledger: CapacityLedger,
): { segment: MachiningSegment; ok: boolean } {
  const hours = stepHours(step);
  let start = end;
  let ok = true;
  const allocations: DailyAllocation[] = [];
  const shares = sharesByMachine(step);

  for (const [machineId, share] of shares) {
    if (share <= EPSILON) continue;
    const placed = placeOnMachine(machineId, share, end, workingDays, dailyCapacity, ledger);
    if (!placed.ok) ok = false;
    if (compareISO(placed.start, start) < 0) start = placed.start;
    allocations.push(...placed.allocations);
  }

  allocations.sort((a, b) => compareISO(a.date, b.date) || (a.machineId < b.machineId ? -1 : a.machineId > b.machineId ? 1 : 0));
  const days = new Set(allocations.map((a) => a.date)).size;
  return {
    segment: {
      kind: "machining",
      stepIndex,
      start,
      end,
      hours,
      workingDays: Math.max(days, 1),
      machines: [...shares].map(([machineId, share]) => ({ machineId, hours: share })),
      allocations,
    },
    ok,
  };
}

/** A step's hours per machine, with any machine listed twice added together. */
function sharesByMachine(step: MachiningStep): Map<string, number> {
  const shares = new Map<string, number>();
  for (const m of step.machines) shares.set(m.machineId, (shares.get(m.machineId) ?? 0) + m.hours);
  return shares;
}

function placeOnMachine(
  machineId: string,
  hours: number,
  end: ISODate,
  workingDays: readonly number[],
  dailyCapacity: ReadonlyMap<string, number>,
  ledger: CapacityLedger,
): { start: ISODate; allocations: DailyAllocation[]; ok: boolean } {
  let remaining = hours;
  const allocations: DailyAllocation[] = [];

  let d = end;
  let scanned = 0;
  for (;;) {
    if (isWorkingDay(d, workingDays)) {
      const avail = availableHoursOn(machineId, d, dailyCapacity, ledger);
      if (avail > EPSILON) {
        const used = Math.min(avail, remaining);
        allocations.push({ date: d, machineId, hours: used });
        remaining -= used;
        if (remaining <= EPSILON) return { start: d, allocations, ok: true };
      }
    }
    scanned++;
    if (scanned > MAX_LOOKBACK_DAYS) {
      // The machine could never fit these hours: it runs no hours, or isn't in the shop's list.
      return { start: d, allocations, ok: false };
    }
    d = addDays(d, -1);
  }
}

interface RiskContext {
  feasible: boolean;
  isComplete: boolean;
  hasStarted: boolean;
  today: ISODate;
  dueDate: ISODate;
  latestStartDate: ISODate;
  slackWorkingDays: number;
  alertLeadDays: number;
}

/** Decision D8 (bands) and D9 (started and complete jobs). */
export function classifyRisk(ctx: RiskContext): RiskLevel {
  if (ctx.isComplete) return "ok";
  if (!ctx.feasible) return "impossible";
  if (ctx.hasStarted) {
    return compareISO(ctx.dueDate, ctx.today) < 0 ? "impossible" : "ok";
  }
  if (compareISO(ctx.latestStartDate, ctx.today) < 0) return "impossible";
  const lead = Math.max(0, ctx.alertLeadDays);
  if (ctx.slackWorkingDays < lead) return "at_risk";
  if (ctx.slackWorkingDays < 2 * lead) return "tight";
  return "ok";
}

function validateSteps(steps: readonly JobStep[]): void {
  steps.forEach((step, i) => {
    if (step.type === "machining") {
      for (const m of step.machines) {
        if (!(m.hours >= 0) || !Number.isFinite(m.hours)) throw new Error(`Step ${i}: hours on ${m.machineId} must be a finite number >= 0`);
      }
    } else if (step.type === "post_process") {
      for (const key of ["leadDays", "transitDaysOut", "transitDaysBack"] as const) {
        const v = step[key];
        if (!Number.isInteger(v) || v < 0) throw new Error(`Step ${i}: ${key} must be a non-negative integer`);
      }
    } else {
      throw new Error(`Step ${i}: unknown step type`);
    }
  });
}
